import { useCallback } from 'react';
import type EventBus from 'diagram-js/lib/core/EventBus';
import type { Event } from 'diagram-js/lib/core/EventBus';
import type { Base } from 'diagram-js/lib/model';

import { useDiagramContext } from '../Context/DiagramContext';
import { useAiEnablerData } from '../service/useAiEnabler';

type ElementEvent = Event & {
  element: Base & {
    id: string;
    type: string;
    businessObject?: { name?: string };
  };
};

function isElementEvent(event: Event): event is ElementEvent {
  return 'element' in event;
}

export const useDiagramEventListeners = () => {
  const {
    bpmnModelerRef,
    diagramData,
    aiOpportunityData,
    isEditPermission,
    setHoveredElements,
    showAllAiComponents,
    setActiveModal,
    setIsDiagramChanged,
    setModal,
    onDiagramChange,
  } = useDiagramContext();

  const { technologyNames } = useAiEnablerData();

  const allowedTypes = [
    'bpmn:Task',
    'bpmn:UserTask',
    'bpmn:ServiceTask',
    'bpmn:SendTask',
    'bpmn:ReceiveTask',
    'bpmn:ManualTask',
    'bpmn:BusinessRuleTask',
    'bpmn:ScriptTask',
    'bpmn:Activity',
    'bpmn:SubProcess',
  ];

  const handleElementHover = useCallback(
    (event: Event) => {
      if (!isElementEvent(event)) return;

      const element = event.element;

      if (allowedTypes.includes(element.type)) {
        setHoveredElements((prev) => {
          const next = new Set(prev);
          next.add(element.id);
          return next;
        });
      }
    },
    [setHoveredElements]
  );

  const handleElementOut = useCallback(
    (event: Event) => {
      if (!isElementEvent(event)) return;

      if (!showAllAiComponents) {
        setHoveredElements((prev) => {
          const next = new Set(prev);
          next.delete(event.element.id);
          return next;
        });
      }
    },
    [showAllAiComponents, setHoveredElements]
  );

  const handleElementClick = useCallback(
    async (event: Event) => {
      if (!isElementEvent(event)) return;

      const element = event.element;

      if (!allowedTypes.includes(element.type) || !diagramData) return;

      // Use stored step description if present; for new elements (e.g. from toolbar) there is no entry yet
      const storedDetails = diagramData.diagram.data?.[element.id];
      const name =
        storedDetails?.name ?? element.businessObject?.name ?? 'Untitled Step';
      const description = storedDetails?.description ?? '';

      const relatedOpportunities = aiOpportunityData.filter((opp) => opp.shape_id === element.id);

      setModal({
        isOpen: true,
        elementData: {
          id: element.id,
          type: element.type,
          name,
          description,
          aiEnablers: technologyNames,
        },
        activeTab: 'aiHubble',
        opportunities: relatedOpportunities,
      });

      setActiveModal('diagram');
    },
    [diagramData, aiOpportunityData, technologyNames, setActiveModal, setModal]
  );

  const handleDiagramChange = useCallback(() => {
    if (isEditPermission) {
      setIsDiagramChanged(true);

      if (onDiagramChange) {
        onDiagramChange(true);
      }
    }
  }, [
    isEditPermission,
    setIsDiagramChanged,
    onDiagramChange,
  ]);

  const attachEventListeners = useCallback(() => {
    if (!bpmnModelerRef.current) return;

    const eventBus = bpmnModelerRef.current.get('eventBus') as EventBus;

    eventBus.on('element.hover', handleElementHover);
    eventBus.on('element.out', handleElementOut);
    eventBus.on('element.click', handleElementClick);

    if (isEditPermission) {
      eventBus.on('commandStack.changed', handleDiagramChange);
    }
  }, [
    bpmnModelerRef,
    handleElementHover,
    handleElementOut,
    handleElementClick,
    handleDiagramChange,
    isEditPermission,
  ]);

  const detachEventListeners = useCallback(() => {
    if (!bpmnModelerRef.current) return;

    const eventBus = bpmnModelerRef.current.get('eventBus') as EventBus;

    eventBus.off('element.hover', handleElementHover);
    eventBus.off('element.out', handleElementOut);
    eventBus.off('element.click', handleElementClick);
    eventBus.off('commandStack.changed', handleDiagramChange);
  }, [
    bpmnModelerRef,
    handleElementHover,
    handleElementOut,
    handleElementClick,
    handleDiagramChange,
  ]);

  return {
    attachEventListeners,
    detachEventListeners,
    handleElementHover,
    handleElementOut,
    handleElementClick,
    handleDiagramChange,
  };
};
